import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Salary } from '@xing-com/crate-jobs-components-salary';
import type {
  JobKeyfactV2Fragment,
  JobMatchingHighlightsV2Fragment,
  JobTeaserData,
} from '@xing-com/crate-jobs-graphql';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import { useAppliedTheme } from '@xing-com/hub';
import { Marker } from '@xing-com/marker';
import { spaceXS } from '@xing-com/tokens';

import { FactMarker } from './fact-marker';

export const MarkerContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spaceXS};

  &:empty {
    display: none;
  }
`;

const FatMarker = styled(Marker)`
  & > * {
    font-weight: bold;
  }
`;

type Props = {
  job: JobTeaserData;
  matchingHighlights: JobMatchingHighlightsV2Fragment | null | undefined;
  showEmploymentType?: boolean;
};

const hasEmploymentTypeFact = (facts: JobKeyfactV2Fragment[]): boolean =>
  facts.findIndex(
    (fact) =>
      fact.__typename === 'JobMatchingHighlightsJobKeyfactStringV2' &&
      fact.type === 'job_keyfact_employment_type'
  ) !== -1;

const hasSalaryFact = (facts: JobKeyfactV2Fragment[]): boolean =>
  facts.findIndex(
    (fact) => fact.__typename === 'JobMatchingHighlightsJobKeyfactSalaryV2'
  ) !== -1;

export const JobTeaserFacts: React.FC<Props> = ({
  job,
  matchingHighlights,
  showEmploymentType,
}) => {
  const isDarkMode = useAppliedTheme() === 'dark';

  const { salary, showSalary, employmentTypeValue } = selectJobData(job);

  const isTakenJob = job.__typename === 'TakenJob';

  const includesEmploymentTypeFact =
    matchingHighlights &&
    (hasEmploymentTypeFact(matchingHighlights.matchingFacts) ||
      hasEmploymentTypeFact(matchingHighlights.nonMatchingFacts));

  const showEmploymentTypeFromJob =
    showEmploymentType && employmentTypeValue && !includesEmploymentTypeFact;

  const includesSalaryFact =
    matchingHighlights &&
    (hasSalaryFact(matchingHighlights.matchingFacts) ||
      hasSalaryFact(matchingHighlights.nonMatchingFacts));

  const showSalaryFromJob = salary && showSalary && !includesSalaryFact;

  const takenJobMarker = isTakenJob ? (
    <FatMarker variant={isDarkMode ? 'onColor' : 'onColorStrong'}>
      <FormattedMessage id={'DREAM_MACHINE_JOB_CARD_TYPE_OCCUPIED_COMPACT'} />
    </FatMarker>
  ) : null;

  const matchingFacts = matchingHighlights
    ? matchingHighlights.matchingFacts.map((fact, i) => (
        <FactMarker key={i} variant="match" fact={fact} />
      ))
    : null;

  const nonMatchingFacts = matchingHighlights
    ? matchingHighlights.nonMatchingFacts.map((fact, i) => (
        <FactMarker key={i} variant="default" fact={fact} />
      ))
    : null;

  const employmentTypeFromJob = showEmploymentTypeFromJob ? (
    <Marker variant="default">{employmentTypeValue}</Marker>
  ) : null;

  const salaryFromJob = showSalaryFromJob ? (
    <Marker variant="default">
      <Salary salary={salary} />
    </Marker>
  ) : null;

  return (
    <MarkerContainer>
      {takenJobMarker}
      {nonMatchingFacts}
      {matchingFacts}
      {employmentTypeFromJob}
      {salaryFromJob}
    </MarkerContainer>
  );
};
